import { defineComponent as _defineComponent } from 'vue'
import { goBack } from '@/composables/useNavigation';
import { SelectOptionsService, toast, toastServiceError } from '@/services';
import WorkScheduleService from '@/services/api/WorkScheduleService';
import { useUiStore } from '@/stores';
import {
    FormAction,
    Permission,
    SelectOptionsServiceGetUsersResult,
    WorkScheduleFormState,
    WorkScheduleModel,
} from '@/types';
import useVuelidate, { Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { AxiosResponse } from 'axios';
import { computed, onMounted, reactive, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkScheduleFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props, { expose: __expose }) {
  __expose();

const uiStore = useUiStore();
const route = useRoute();
const { t } = useI18n();

const props = __props;

const formState = reactive<WorkScheduleFormState>({
    name: null,
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    timeBegin: '08:00:00',
    timeEnd: '17:00:00',
    isActive: true,
    users: [],
});

const rules = computed<ValidationArgs<WorkScheduleFormState>>(() => ({
    name: { required, maxLength: maxLength(50) },
    monday: { required },
    tuesday: { required },
    wednesday: { required },
    thursday: { required },
    friday: { required },
    saturday: { required },
    sunday: { required },
    timeBegin: { required },
    timeEnd: { required },
    isActive: { required },
    users: {},
}));

const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const v$: Ref<Validation<ValidationArgs<WorkScheduleFormState>>> = useVuelidate(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchWorkScheduleById = async (id: number) => {
    console.log(`buscando item id ${id}`);
    try {
        setLoading(true);
        const { data } = await WorkScheduleService.get<WorkScheduleModel>(id);
        Object.assign(formState, data);
        formState.users = (data.users || []).map((user) => user.id);
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                has_extension: true,
                is_active: true,
            },
        });
        usersOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const isTimeBefore = (start: string, end: string): boolean => {
    const [h1, m1] = start.split(':').map(Number);
    const [h2, m2] = end.split(':').map(Number);
    return h1 < h2 || (h1 == h2 && m1 < m2);
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    const noDaySelected = !(
        formState.monday ||
        formState.tuesday ||
        formState.wednesday ||
        formState.thursday ||
        formState.friday ||
        formState.saturday ||
        formState.sunday
    );

    if (noDaySelected) {
        toastServiceError(t('workSchedule.error.dayNotSelected'));
        return;
    }

    if (!isTimeBefore(formState.timeBegin, formState.timeEnd)) {
        toastServiceError(t('workSchedule.error.invalidTimeRange'));
        return;
    }

    try {
        setLoading(true);
        let response: AxiosResponse;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await WorkScheduleService.update(id, formState);
            toast.success(response.data.message);
            goBack({ name: 'ViewWorkScheduleById', params: { id } });
        } else {
            response = await WorkScheduleService.create(formState);
            toast.success(response.data.message);
            goBack({ name: 'ViewWorkScheduleById', params: { id: response.data.result.id } });
        }

        v$.value.$reset();
    } catch (error) {
        toastServiceError(error);
    } finally {
        setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        const workScheduleId = Number(route.params.id);
        if (!isNaN(workScheduleId)) fetchWorkScheduleById(workScheduleId);
    }
});

const __returned__ = { uiStore, route, t, props, formState, rules, usersOptions, v$, setLoading, fetchWorkScheduleById, fetchUsers, isTimeBefore, onSubmit, get FormAction() { return FormAction }, get Permission() { return Permission } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})